import { Dictionary, keysFrom, TrackingScopes } from '@moda/portal-astrarium';
import { FeatureFlag } from '@moda/portal-stanchions';
import { always, head, ifElse, isNil, pipe, pluck, reject, uniq } from 'ramda';
import { Cart } from '../hooks/useCart';
import { UseUserQuery_user as User } from '../generated/types/UseUserQuery';
import { isClientSide } from './isClientSide';
// pretty much every property that Segment asks for is in snake_case.

/**
findVariantInventoryType: This function is here to help with GTM tags.
It returns "Boutique" if the product in scope is a boutique product, or "Trunkshow" otherwise.
These values are intentionally Title Case.
*/
export const findVariantInventoryType = (scope: TrackingScopes) => {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const isBoutique: boolean | undefined = (scope?.variant as Record<string, any> | undefined)
    ?.isBoutique;
  if (isBoutique === undefined) return undefined;
  return isBoutique ? 'Boutique' : 'Trunkshow';
};

/**
findCartItemInventoryType: This function is here to help with GTM tags.
It returns "boutique" if the product in scope is a boutique product, or "trunkshow" otherwise.
These values are intentionally lowercase.
*/
const findCartItemInventoryType = (scope: TrackingScopes) => {
  // `scope` here is the secondary scope, as surfaced in the `properties` of a ListLocator.
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const isTrunkshow = (scope?.attributes as Record<string, any> | undefined)?.isTrunkshow;
  if (isTrunkshow === undefined) return undefined;
  return isTrunkshow ? 'trunkshow' : 'boutique';
};

/**
getSearchIndex: This function is here to help with Algolia analytics
For product lists, the index that the product item came from is included with
each product.  The way we use Algolia, searches always come from one index at a time.
So, we only need to find the first item in order to pull out the index used.
*/
const getSearchIndex = (scope: TrackingScopes) => {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const products = scope?.products as Record<string, any>[] | undefined;
  if (!products?.length) return undefined;
  return products[0].index;
};

/**
getBrandForBrandedPLP: This function is here to help with GTM tags.
If the PLP is all from one designer, return that designer as the brand name.
If the PLP is _not_ all from one designer, then set the brand to "" (empty string).
Otherwise, return undefined - thereby not setting the brand.
*/
const getBrandForBrandedPLP = (scope: TrackingScopes) => {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const products = scope?.products as Record<string, any>[] | undefined;
  if (products === undefined) return undefined;
  const brand: string | undefined = pipe(
    pluck('designerName'),
    uniq,
    reject(isNil),
    ifElse(brands => brands.length > 1, always(''), head)
  )(products);
  return brand;
};

const stringifyFlagValue = function (value: FeatureFlag) {
  if (typeof value === 'object') return JSON.stringify(value);
  if (typeof value === 'number' || typeof value === 'boolean') return String(value);
  return value;
};

const loginTypeDefinition = (user: User): string => {
  let loginType = '';
  switch (true) {
    case user.isAmazonConnected:
      loginType = 'amz-login';
      break;
    case user.isFacebookConnected:
      loginType = 'fb-login';
      break;
    case user.isLoggedIn:
      loginType = 'moda-login';
      break;
    case !user.isLoggedIn:
      loginType = 'guest';
      break;
  }
  return loginType;
};

type FlagScope = { flagName: string; treatment: FeatureFlag };

const definitions = {
  product_id: ['variant.productId', 'lineItem.attributes.productId'],
  variant: ['variant.id', 'lineItem.attributes.variantId'],
  sku: ['size.sku', 'lineItem.attributes.skuId'],
  name: ['variant.name', 'lineItem.attributes.productName'],
  brand: ['variant.designerName', 'lineItem.attributes.designerName', getBrandForBrandedPLP],
  price: [
    'activeVariant.prices.currentPrice.price',
    'lineItem.attributes.prices.currentPrice.price'
  ],
  original_price: [
    'activeVariant.prices.originalPrice.price',
    'lineItem.attributes.prices.originalPrice.price'
  ],
  image_url: [
    'activeVariant.primaryImage.medium',
    'activeVariant.primaryImageUrls.src',
    'lineItem.attributes.imageSrc'
  ],
  product_availability: ['activeVariant.availability'],
  currency: [
    'activeVariant.prices.currentPrice.currency',
    'lineItem.attributes.prices.currentPrice.currency',
    'order.attributes.currencyUnit',
    'cart.attributes.currency',
    'order.attributes.currency'
  ],
  inventory_type: [findVariantInventoryType],
  filters: [
    {
      listAt: ['filters'],
      properties: {
        type: ['type'],
        value: ['value']
      }
    }
  ],
  sorts: ['sorts'],
  products: [
    {
      listAt: ['products'],
      properties: {
        product_id: ['productId', 'attributes.productId'],
        variant: ['id', 'attributes.variantId'],
        name: ['name', 'attributes.productName'],
        brand: ['designerName', 'attributes.designerName'],
        price: ['masterVariant.prices.currentPrice.price', 'attributes.prices.currentPrice.price'],
        original_price: [
          'masterVariant.prices.originalPrice.price',
          'attributes.prices.originalPrice.price'
        ],
        image_url: ['masterVariant.primaryImageUrls.src', 'attributes.imageSrc'],
        product_availability: ['availability'],
        currency: [
          'masterVariant.prices.currentPrice.currency',
          'attributes.prices.currentPrice.currency'
        ],
        inventory_type: [findCartItemInventoryType],
        quantity: ['attributes.quantity'],
        sku: ['attributes.skuId'],
        objectID: ['objectid']
      }
    }
  ],
  cart_id: ['cart.id'],
  cart_type: ['cart.attributes.version'],
  order_id: ['order.id'],
  order_number: ['order.attributes.orderNumber'],
  revenue: [
    ({ cart }: TrackingScopes) =>
      Number((cart as Cart | undefined)?.attributes.paymentSummary.total.price || 0) -
      Number((cart as Cart | undefined)?.attributes.paymentSummary.totalShipping.price || 0) -
      Number((cart as Cart | undefined)?.attributes.paymentSummary.totalTaxes.price || 0)
  ],
  total: [
    'order.attributes.paymentSummary.total.price',
    'cart.attributes.paymentSummary.total.price'
  ],
  paid_remaining_balance: ['order.attributes.paymentSummary.orderBalance'],
  shipping: [
    'order.attributes.paymentSummary.shippingAndPackaging.price',
    'cart.attributes.paymentSummary.esimatedShipping.price'
  ],
  tax: [
    'order.attributes.paymentSummary.salesTaxesAndDuties.price',
    'cart.attributes.paymentSummary.totalTaxes.price'
  ],
  subtotal: [
    'cart.attributes.paymentSummary.subtotal.price',
    'order.attributes.totals.orderSubtotal'
  ],
  coupon: ['cart.attributes.giftCode', 'order.attributes.paymentSummary.giftCode'],
  discount: ['order.attributes.paymentSummary.discounts.price'],
  checkout_step: ['checkoutStep'],
  shipping_method: [
    ({ cart }: TrackingScopes) =>
      (cart as Cart | undefined)?.attributes.shippingMethods?.find(
        ({ method }) => method === (cart as Cart | undefined)?.attributes.selectedShippingMethodId
      )?.displayName
  ],
  payment_method: ['cart.attributes.paymentSource'],
  first_order: ['order.attributes.firstOrder'],
  query: ['query'],
  search_type: ['searchType'],
  cj_aid: ['referralParameters.aid'],
  cj_pid: ['referralParameters.pid'],
  cj_sid: ['referralParameters.sid'],
  cj_event: ['referralParameters.cjevent'],
  utm_source: ['referralParameters.utm_source'],
  experiment_id: ['featureFlag.flagName'],
  variation_id: [
    ({ featureFlag }: TrackingScopes) =>
      JSON.stringify((featureFlag as { treatment: FeatureFlag })?.treatment)
  ],
  label: [
    (scopes: TrackingScopes) => {
      const flag = scopes.featureFlag as FlagScope;
      return `${flag.flagName}=${stringifyFlagValue(flag.treatment)}`;
    }
  ],
  category: ['gaEventCategory'],
  badge: ['badge'],
  signed_in: [
    ({ user }: TrackingScopes) => ((user as User)?.isLoggedIn ? 'signed in' : 'not signed in')
  ],
  email: ['user.email'],
  first_name: ['user.firstName'],
  last_name: ['user.lastName'],
  country_code: ['user.countryCode'],
  module_name: ['moduleName'],
  designer_name: ['designerName'],
  designer_id: ['designerId'],
  vertical: ['vertical'],
  login_type: [({ user }: TrackingScopes) => loginTypeDefinition(user as User)],
  index: ['variant.index', getSearchIndex],
  objectID: ['objectid', 'variant.objectid']
};

const propertyGroups = {
  Product: keysFrom<typeof definitions>([
    'product_id',
    'variant',
    'sku',
    'name',
    'brand',
    'price',
    'original_price',
    'image_url',
    'product_availability',
    'currency',
    'inventory_type',
    'badge',
    'index',
    'objectID'
  ]),
  Products: keysFrom<typeof definitions>(['products', 'index']),
  Filters: keysFrom<typeof definitions>(['filters']),
  Sorts: keysFrom<typeof definitions>(['sorts']),
  Cart: keysFrom<typeof definitions>(['cart_id', 'cart_type']),
  Order: keysFrom<typeof definitions>([
    'order_id',
    'order_number',
    'total',
    'currency',
    'shipping',
    'tax',
    'first_order',
    'coupon',
    'discount',
    'subtotal',
    'shipping_method'
  ]),
  SecondPayment: keysFrom<typeof definitions>([
    'order_id',
    'order_number',
    'paid_remaining_balance',
    'currency',
    'coupon',
    'discount'
  ]),
  Checkout: keysFrom<typeof definitions>([
    'subtotal',
    'total',
    'shipping',
    'tax',
    'coupon',
    'currency',
    'revenue'
  ]),
  CheckoutStep: keysFrom<typeof definitions>([
    'checkout_step',
    'shipping_method',
    'payment_method'
  ]),
  SearchInfo: keysFrom<typeof definitions>(['query', 'search_type']),
  ListBrand: keysFrom<typeof definitions>(['brand']),
  CJReferral: keysFrom<typeof definitions>([
    'cj_aid',
    'cj_pid',
    'cj_sid',
    'cj_event',
    'utm_source'
  ]),
  Experiment: keysFrom<typeof definitions>(['experiment_id', 'variation_id', 'label', 'category']),
  User: keysFrom<typeof definitions>([
    'email',
    'first_name',
    'last_name',
    'country_code',
    'login_type'
  ]),
  Designer: keysFrom<typeof definitions>(['designer_name', 'designer_id', 'vertical']),
  SignedIn: keysFrom<typeof definitions>(['signed_in']),
  Module: keysFrom<typeof definitions>(['module_name'])
};

const events = {
  // Make sure that all events containing "Product" or "Products" happen _after_
  // the page event for the page that triggers them, or else GTM will be missing
  // information that it needs to fire tags with correct information.
  // Use the deferUntilNewTitle function with tc.track to make sure that the
  // timing is correct.
  'Designer Followed': keysFrom<typeof propertyGroups>(['Designer']),
  'Designer Unfollowed': keysFrom<typeof propertyGroups>(['Designer']),
  'Interacted with Form': keysFrom<typeof propertyGroups>([]),
  'Payment Error Encountered': keysFrom<typeof propertyGroups>([]),
  'Signed In': keysFrom<typeof propertyGroups>(['User']),
  'Signed Out': keysFrom<typeof propertyGroups>(['User']),
  'Breadcrumb Clicked': keysFrom<typeof propertyGroups>([]),
  'Navigation Item Clicked': keysFrom<typeof propertyGroups>([]),
  'Mini Cart Icon Clicked': keysFrom<typeof propertyGroups>(['Cart']),
  'Mini Cart Proceed To Shopping Bag Clicked': keysFrom<typeof propertyGroups>(['Cart']),
  'Module Viewed': keysFrom<typeof propertyGroups>(['Module']),
  'Module Clicked': keysFrom<typeof propertyGroups>(['Module']),
  'Product Viewed': keysFrom<typeof propertyGroups>(['Product', 'SignedIn']),
  'Product Clicked': keysFrom<typeof propertyGroups>(['Product', 'Module']),
  'Product List Viewed': keysFrom<typeof propertyGroups>(['Products', 'ListBrand']),
  'Product List Filtered': keysFrom<typeof propertyGroups>([
    'Filters',
    'Sorts',
    'Products',
    'ListBrand'
  ]),
  'Product Added': keysFrom<typeof propertyGroups>(['Product', 'Cart', 'Products']),
  'Product Removed': keysFrom<typeof propertyGroups>(['Product', 'Cart']),
  'Products Searched': keysFrom<typeof propertyGroups>(['SearchInfo']),
  'Order Converted': keysFrom<typeof propertyGroups>(['Order', 'Products', 'CJReferral']),
  'Second Payment Completed': keysFrom<typeof propertyGroups>(['SecondPayment', 'Products']),
  'Product Added to Wishlist': keysFrom<typeof propertyGroups>(['Product']),
  'Product Removed from Wishlist': keysFrom<typeof propertyGroups>(['Product']),
  'Wishlist Product Added to Cart': keysFrom<typeof propertyGroups>(['Product', 'Cart']),
  'Cart Viewed': keysFrom<typeof propertyGroups>(['Cart', 'Products']),
  'Checkout Started': keysFrom<typeof propertyGroups>(['Checkout', 'Cart', 'Products', 'User']),
  'Checkout Step Viewed': keysFrom<typeof propertyGroups>([
    'Cart',
    'Checkout',
    'Products',
    'CheckoutStep'
  ]),
  'Checkout Step Completed': keysFrom<typeof propertyGroups>([
    'Cart',
    'Checkout',
    'CheckoutStep',
    'Products'
  ]),
  'Promotion Clicked': keysFrom<typeof propertyGroups>([]),
  'Payment Info Entered': keysFrom<typeof propertyGroups>(['Cart', 'CheckoutStep']),
  'Registration Completed': keysFrom<typeof propertyGroups>([]),
  'Experiment Viewed': keysFrom<typeof propertyGroups>(['Experiment']),
  'Welcome Email Modal Viewed': keysFrom<typeof propertyGroups>([]),
  'Welcome Email Modal Sign Up Finished': keysFrom<typeof propertyGroups>([]),
  'Welcome Email Modal Dismissed': keysFrom<typeof propertyGroups>([]),
  'Third Party Payment Method Flow Selected': keysFrom<typeof propertyGroups>([]),
  'Largest Contentful Paint': keysFrom<typeof propertyGroups>([]),
  'First Input Delay': keysFrom<typeof propertyGroups>([]),
  'Cumulative Layout Shift': keysFrom<typeof propertyGroups>([])
};

const featureFlags = isClientSide() ? window.__FEATURE_FLAGS__ || {} : {};

const appendToEveryEvent = {
  srx: true,
  event_source: 'astrarium',
  active_feature_flags: Object.keys(featureFlags)
    .filter(key => featureFlags[key] !== false && featureFlags[key] != null)
    .map(key => {
      const value = featureFlags[key];
      if (typeof value === 'object') return `${key}=${JSON.stringify(value)}`;
      if (typeof value === 'number') return `${key}=${String(value)}`;
      if (typeof value === 'string') return `${key}=${value}`;
      return key;
    })
    .sort()
    .join(',')
};

export const dictionary = new Dictionary<
  keyof typeof events,
  keyof typeof propertyGroups,
  keyof typeof definitions
>(events, propertyGroups, definitions, appendToEveryEvent);
